import React from "react";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import BayHireAttachmentsSection from "@features/bay-hire/components/BayHireRequestDetail/BayHireAttachmentsSection";
import BayHireFileCard from "@features/bay-hire/components/BayHireRequestDetail/BayHireFileCard";
import { getAttachmentFileName } from "@features/bay-hire/hooks/useDownloadBayHireAttachment";
import type { Attachment } from "@services/bayHireApi";
import { useTranslation } from "react-i18next";

interface PreApprovedSummaryDialogProps {
  open: boolean;
  chargeable?: boolean | null;
  totalAmount?: number | null;
  preApprovalRemarks?: string | null;
  hodApprovalAttachment: Attachment | null;
  /**
   * DFT-0104: the relocated Pre-approve SUPPORTING_DOCUMENT attachment, now that
   * PAYED/APPROVED also reuse this drawer (extending its original PRE_APPROVED-only scope).
   * Optional/defaults to empty so this remains a no-op for any caller not yet passing it.
   */
  attachments?: Attachment[];
  isDownloading: (documentUrl: string) => boolean;
  /** Opens the HOD document in a new tab (view) - AC29. */
  onView: (attachment: Attachment) => void;
  onDownload: (attachment: Attachment) => void;
  onClose: () => void;
}

/**
 * DFT-0044 slice F (FE-38, a presumption - flag in the PR for design review): a read-only summary
 * of a PRE_APPROVED request's own decision (Chargeable, Total Amount, the HOD document, Remarks),
 * reached from the status row's "- View details" link, matching DetailsDialog's own equivalent for
 * REJECTED/CANCELLED.
 *
 * No wireframe in either drop (`PoF Visuals _ bay_hire_updated_jul26` or the newer
 * `PoF Visuals_Bay Hire 2`) shows this screen - `05_Port_License_details-19.png`'s "Pre-approve Bay
 * Hire" drawer is the EDITABLE form Operations fills in to produce this decision, not a read-only
 * view of it (checked again this session before building this). The field set (Chargeable/Total
 * Amount/HOD document/Remarks) is inferred from that editable drawer's own fields, since a
 * decision's read-only summary showing less than what was captured to make it would be a stranger
 * guess than showing all of it.
 */
const PreApprovedSummaryDialog: React.FC<PreApprovedSummaryDialogProps> = ({
  open,
  chargeable,
  totalAmount,
  preApprovalRemarks,
  hodApprovalAttachment,
  attachments = [],
  isDownloading,
  onView,
  onDownload,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <div data-testid="pre-approved-summary-dialog">
      <SideDrawer
        isOpen={open}
        title={t("bay-hire.pre-approve.summary-title")}
        onClose={onClose}
        onSubmit={onClose}
        submitLabel={t("common.ok", { defaultValue: "OK" })}
        hideCancelButton
        // DFT-0083: same fit-content/max-content defect as DetailsDialog.tsx - see its comment
        // for the mechanism. Fixed here to this drawer only, not SimpleSideDrawer's shared
        // default.
        width={400}
      >
        <div className="flex flex-col gap-4 px-6 py-2">
          <div>
            <h4 className="text-xs font-medium text-gray-500 mb-1">
              {t("bay-hire.detail.preApprovedAmount")}
            </h4>
            <p className="text-sm text-gray-900" data-testid="pre-approved-summary-total-amount">
              {totalAmount != null ? totalAmount : "-"} (
              {chargeable ? t("bay-hire.detail.chargeable") : t("bay-hire.detail.notChargeable")})
            </p>
          </div>

          <div>
            <h4 className="text-xs font-medium text-gray-500 mb-1">
              {t("bay-hire.pre-approve.hod-section-title")}
            </h4>
            {hodApprovalAttachment ? (
              <BayHireFileCard
                fileName={getAttachmentFileName(hodApprovalAttachment)}
                caption={hodApprovalAttachment.attachmentType}
                isDownloading={isDownloading(hodApprovalAttachment.documentUrl)}
                onView={() => onView(hodApprovalAttachment)}
                onDownload={() => onDownload(hodApprovalAttachment)}
                testId="pre-approved-summary-hod-file"
              />
            ) : (
              <p className="text-sm text-gray-500" data-testid="pre-approved-summary-no-hod">
                {t("bay-hire.pre-approve.no-hod-document")}
              </p>
            )}
          </div>

          {/* DFT-0104: the relocated Pre-approve supporting-document attachment, reusing the
              exact component DetailsDialog already uses for Cancel/Reject's own relocated
              attachment - no new heading copy or layout invented. */}
          {attachments.length > 0 && (
            <BayHireAttachmentsSection
              attachments={attachments}
              isDownloading={isDownloading}
              onView={onView}
              onDownload={onDownload}
            />
          )}

          <div>
            <h4 className="text-xs font-medium text-gray-500 mb-1">
              {t("bay-hire.pre-approve.preApprovalRemarks")}
            </h4>
            <p
              className="text-sm text-gray-900 break-words"
              data-testid="pre-approved-summary-remarks"
            >
              {preApprovalRemarks || t("bay-hire.detail.noRemarks")}
            </p>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default PreApprovedSummaryDialog;
